import React, { useState, useEffect } from 'react';
import '../styles/ResumeForm.css';

const ResumeEditForm = ({
  fields,
  onChange,
  onSave,
  initialExpandedSection = null,
  showOnlySections = null,
  // Add the new props
  onToggleCompany,
  onDeleteCompany,
  onMoveCompanyUp,
  onMoveCompanyDown,
  onAddCompany,
  onAddRole,
  onToggleRole,
  onDeleteRole,
  onMoveRoleUp,
  onMoveRoleDown,
  onAddBullet,
  onToggleBullet,
  onDeleteBullet,
  onCompanyChange,
  onRoleChange,
  onBulletChange,
  onToggleEducation,
  onDeleteEducation,
  onMoveBulletUp,
  onMoveBulletDown,
  expandedCompanies,
  setExpandedCompanies,
  expandedRoles,
  setExpandedRoles
}) => {
  // State to track which sections are expanded/collapsed
  const [expandedSections, setExpandedSections] = useState({
    personal: false,
    summary: false,
    experience: false,
    projects: false,
    skills: false,
    education: false
  });

  // First, let's update the state to track expanded companies and roles
  //const [expandedCompanies, setExpandedCompanies] = useState({});
  //const [expandedRoles, setExpandedRoles] = useState({});
  const [expandedProjects, setExpandedProjects] = useState({});


  // Function to convert text to Title Case
  const toTitleCase = (text) => {
    if (!text) return '';
    return text
      .toLowerCase()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // If initialExpandedSection is provided, expand that section
  useEffect(() => {
    if (initialExpandedSection) {
      setExpandedSections(prev => ({
        ...prev,
        [initialExpandedSection]: true
      }));
    }
  }, [initialExpandedSection]);

  // Toggle section expansion
  const toggleSection = (section) => {
    setExpandedSections({
      ...expandedSections,
      [section]: !expandedSections[section]
    });
  };

  // Then add toggle functions for companies and roles
  const toggleCompany = (companyIndex) => {
    setExpandedCompanies(prev => ({
      ...prev,
      [companyIndex]: !prev[companyIndex]
    }));
  };

  const toggleRole = (companyIndex, roleIndex) => {
    const key = `${companyIndex}-${roleIndex}`;
    setExpandedRoles(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const toggleProject = (projectIndex) => {
    setExpandedProjects(prev => ({
      ...prev,
      [projectIndex]: !prev[projectIndex]
    }));
  };

  // Handle updating top-level fields
  const updateField = (fieldName, value) => {
    onChange({
      ...fields,
      [fieldName]: value
    });
  };


  // Initialize companies if they don't exist
  if (!fields.companies) {
    onChange({
      ...fields,
      companies: [{
        name: '',
        location: '',
        roles: [{
          title: '',
          start_year: '',
          end_year: '',
          bullets: ['']
        }]
      }]
    });
  }

  // Chevron icon component (SVG)
  const ChevronIcon = ({ expanded }) => (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{
        transform: expanded ? 'rotate(0deg)' : 'rotate(180deg)',
        transition: 'transform 0.3s ease'
      }}
    >
      <polyline points="6 9 12 15 18 9"></polyline>
    </svg>
  );

  // Section header component
  // Section header component - updated version
  const SectionHeader = ({ title, section, expanded }) => (
    <div
      className={`resume-form-section-header ${expanded ? 'expanded' : ''}`}
      onClick={() => toggleSection(section)}
    >
      <h4>{title}</h4>
      <ChevronIcon expanded={!expanded} />
    </div>
  );

  // Add this to ResumeEditForm.js
  useEffect(() => {
    // Use a different condition to detect when fields are reset
    // For example, check if all main fields were cleared at once
    if (!fields.first_name && !fields.last_name && !fields.email && !fields.phone) {
      setExpandedSections({
        personal: false,
        summary: false,
        experience: false,
        projects: false,
        skills: false,
        education: false
      });

      if (setExpandedCompanies && setExpandedRoles) {
        setExpandedCompanies({});
        setExpandedRoles({});
      }
      if (setExpandedProjects) {
        setExpandedProjects({});
      }
    }
  }, [fields]); // Use the entire fields object as dependency

  // Check if projects section should be displayed
  const hasProjects = fields.projects && Array.isArray(fields.projects) && fields.projects.length > 0;


  // Update the project name field to convert input to Title Case on change
  const updateProjectName = (projectIndex, value) => {
    const newProjects = [...fields.projects];
    newProjects[projectIndex].name = value;
    updateField('projects', newProjects);
  };

  // Function to toggle omitting a project
  const toggleOmitProject = (projectIndex, e) => {
    // Prevent the click from toggling the project expansion
    e.stopPropagation();

    // Update the projects array with omitted flag
    const newProjects = [...fields.projects];
    newProjects[projectIndex].omitted = !newProjects[projectIndex].omitted;
    updateField('projects', newProjects);
  };

  return (
    <form
      onSubmit={e => {
        e.preventDefault();
        onSave(fields, false);
      }}
      className="resume-edit-form"
    >
      {!showOnlySections && <h3>Edit Resume</h3>}

      {/* Personal Information Section */}
      {(!showOnlySections || showOnlySections.includes("personal")) && (
        <div className="company-section">
          <SectionHeader
            title="Personal Information"
            section="personal"
            expanded={expandedSections.personal}
          />

          {expandedSections.personal && (
            <div className="resume-form-section-content">
              <div className="form-group">
                <label>First Name:</label>
                <input
                  type="text"
                  value={fields.first_name || ''}
                  onChange={e => updateField('first_name', e.target.value)}
                  placeholder="Your first name"
                />
              </div>

              <div className="form-group">
                <label>Last Name:</label>
                <input
                  type="text"
                  value={fields.last_name || ''}
                  onChange={e => updateField('last_name', e.target.value)}
                  placeholder="Your last name"
                />
              </div>

              <div className="form-group">
                <label>Phone:</label>
                <input
                  type="text"
                  value={fields.phone || ''}
                  onChange={e => updateField('phone', e.target.value)}
                  placeholder="Your phone number"
                />
              </div>

              <div className="form-group">
                <label>Email:</label>
                <input
                  type="email"
                  value={fields.email || ''}
                  onChange={e => updateField('email', e.target.value)}
                  placeholder="Your email"
                />
              </div>

              <div className="form-group">
                <label>LinkedIn:</label>
                <input
                  type="text"
                  value={fields.linkedin_link || ''}
                  onChange={e => updateField('linkedin_link', e.target.value)}
                  placeholder="Your LinkedIn profile URL"
                />
              </div>
            </div>
          )}
        </div>
      )}

      {/* Summary Section */}
      {(!showOnlySections || showOnlySections.includes("summary")) && (
        <div className="company-section">
          <SectionHeader
            title="Professional Summary"
            section="summary"
            expanded={expandedSections.summary}
          />

          {expandedSections.summary && (
            <div className="resume-form-section-content">
              <div className="form-group">
                <textarea
                  value={fields.professional_summary || ''}
                  onChange={e => updateField('professional_summary', e.target.value)}
                  placeholder="Brief professional summary"
                  rows={4}
                />
              </div>
            </div>
          )}
        </div>
      )}

      {/* Experience Section */}
      {(!showOnlySections || showOnlySections.includes("experience")) && (
        <div className="company-section">
          <SectionHeader
            title="Work Experience"
            section="experience"
            expanded={expandedSections.experience}
          />

          {expandedSections.experience && (
            <div className="resume-form-section-content">
              {fields.companies && fields.companies.map((company, companyIndex) => (
                <div key={`company-${companyIndex}`} className="company-section" style={{ opacity: company.omitted ? 0.5 : 1 }}>
                  <div className="company-header">
                    <div className="company-header-left" onClick={() => toggleCompany(companyIndex)}>
                      <ChevronIcon expanded={!expandedCompanies[companyIndex]} />
                      <h4>{company.name || 'Company'}</h4>
                    </div>
                    <div className="company-header-right">
                      <div className="action-buttons">

                        <div className="toggle-container">
                          <label className="toggle-switch" title={company.omitted ? "Include in resume" : "Exclude from resume"}>
                            <input
                              type="checkbox"
                              checked={!company.omitted}
                              onChange={(e) => {
                                e.stopPropagation();
                                onToggleCompany(companyIndex);
                              }}
                            />
                            <span className="slider"></span>
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>

                  {expandedCompanies[companyIndex] && (
                    <div className="company-details">
                      <div className="company-section-form-group">
                        <label>Company Name:</label>
                        <input
                          type="text"
                          value={company.name || ''}
                          onChange={e => onCompanyChange(companyIndex, 'name', e.target.value)}
                          placeholder="Company Name"
                          className="form-input"
                        />
                      </div>

                      <div className="company-section-form-group">
                        <label>Location:</label>
                        <input
                          type="text"
                          value={company.location || ''}
                          onChange={e => onCompanyChange(companyIndex, 'location', e.target.value)}
                          placeholder="City, State"
                          className="form-input"
                        />
                      </div>

                      <div className="company-section-form-group">
                        <label>Roles:</label>
                        {company.roles && company.roles.map((role, roleIndex) => {
                          const roleKey = `${companyIndex}-${roleIndex}`;
                          return (
                            <div key={`role-${roleKey}`} className="role-section" style={{ opacity: role.omitted ? 0.5 : 1 }}>
                              <div className="role-header">
                                <div className="role-header-left" onClick={() => toggleRole(companyIndex, roleIndex)}>
                                  <ChevronIcon expanded={!expandedRoles[roleKey]} />
                                  <h5>{role.title || ''}</h5>
                                </div>
                                <div className="role-header-right">

                                  <div className="action-buttons">

                                    <div className="toggle-container">
                                      <label className="toggle-switch" title={role.omitted ? "Include in resume" : "Exclude from resume"}>
                                        <input
                                          type="checkbox"
                                          checked={!role.omitted}
                                          onChange={(e) => {
                                            e.stopPropagation();
                                            onToggleRole(companyIndex, roleIndex);
                                          }}
                                        />
                                        <span className="slider"></span>
                                      </label>
                                    </div>
                                  </div>
                                </div>
                              </div>

                              {expandedRoles[roleKey] && (
                                <div className="role-details">
                                  <div className="role-section-form-group">
                                    <label>Role Title:</label>
                                    <input
                                      type="text"
                                      value={role.title || ''}
                                      onChange={e => onRoleChange(companyIndex, roleIndex, 'title', e.target.value)}
                                      placeholder="Role Title"
                                      className="resume-form-input"
                                    />
                                  </div>

                                  <div className="resume-form-row">
                                    <div className="form-group half-width">
                                      <label>Start Year:</label>
                                      <input
                                        type="text"
                                        value={role.start_year || ''}
                                        onChange={e => onRoleChange(companyIndex, roleIndex, 'start_year', e.target.value)}
                                        placeholder="YYYY"
                                        className="resume-form-input"
                                      />
                                    </div>

                                    <div className="form-group half-width">
                                      <label>End Year:</label>
                                      <input
                                        type="text"
                                        value={role.end_year || ''}
                                        onChange={e => onRoleChange(companyIndex, roleIndex, 'end_year', e.target.value)}
                                        placeholder="YYYY or Present"
                                        className="resume-form-input"
                                      />
                                    </div>
                                  </div>

                                  <div className="form-group">
                                    <label>Responsibilities:</label>
                                    {role.bullets && role.bullets.map((bullet, bulletIndex) => (
                                      <div key={`bullet-${companyIndex}-${roleIndex}-${bulletIndex}`} className="bullet-input-container">
                                        <div className="bullet-move-buttons">
                                          <button
                                            className="move-button"
                                            onClick={() => onMoveBulletUp(companyIndex, roleIndex, bulletIndex)}
                                            title="Move responsibility up"
                                            disabled={bulletIndex === 0}
                                          >
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                              <path d="M12 19V5M5 12l7-7 7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                          </button>
                                          <button
                                            className="move-button"
                                            onClick={() => onMoveBulletDown(companyIndex, roleIndex, bulletIndex)}
                                            title="Move responsibility down"
                                            disabled={bulletIndex === role.bullets.length - 1}
                                          >
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                              <path d="M12 5v14M5 12l7 7 7-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                          </button>
                                        </div>
                                        <textarea
                                          value={typeof bullet === 'string' ? bullet : bullet.text || ''}
                                          onChange={e => onBulletChange(companyIndex, roleIndex, bulletIndex, e.target.value)}
                                          placeholder="Describe your responsibility or achievement"
                                          rows={2}
                                          className={typeof bullet === 'object' && bullet.omitted ? 'omitted' : ''}
                                        />
                                        <div className="toggle-container">
                                          <label className="toggle-switch" title={(typeof bullet === 'object' && bullet.omitted) ? "Include in resume" : "Exclude from resume"}>
                                            <input
                                              type="checkbox"
                                              checked={!(typeof bullet === 'object' && bullet.omitted)}
                                              onChange={() => onToggleBullet(companyIndex, roleIndex, bulletIndex)}
                                            />
                                            <span className="slider"></span>
                                          </label>

                                        </div>
                                      </div>
                                    ))}

                                    <button
                                      className="add-bullet-button"
                                      onClick={() => onAddBullet(companyIndex, roleIndex)}
                                    >
                                      <span className="plus-icon">+</span> Add Responsibility
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}

                        <button className="add-role-button" onClick={() => onAddRole(companyIndex)}>
                          <span className="plus-icon">+</span> Add Role
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}

              <button className="add-company-button" onClick={onAddCompany}>
                <span className="plus-icon">+</span> Add Company
              </button>
            </div>
          )}
        </div>
      )}

      {/* Projects Section */}
      {(!showOnlySections || showOnlySections.includes("projects")) && hasProjects && (
        <div className="company-section">
          <SectionHeader
            title="Projects"
            section="projects"
            expanded={expandedSections.projects}
          />

          {expandedSections.projects && (
            <div className="resume-form-section-content">
              {fields.projects.map((project, projectIndex) => (
                <div key={`project-${projectIndex}`} className="company-section" style={{ opacity: project.omitted ? 0.5 : 1 }}>
                  <div className="company-header">
                    <div className="header-content" onClick={() => toggleProject(projectIndex)}>
                      <ChevronIcon expanded={expandedProjects[projectIndex]} />
                      <h4>{toTitleCase(project.name) || 'New Project'}</h4>
                    </div>
                    <div className="toggle-container">
                      <label className="toggle-switch" title={project.omitted ? "Include in resume" : "Exclude from resume"}>
                        <input
                          type="checkbox"
                          checked={!project.omitted}
                          onChange={(e) => {
                            e.stopPropagation();
                            toggleOmitProject(projectIndex, e);
                          }}
                        />
                        <span className="slider"></span>
                      </label>

                    </div>
                  </div>

                  {expandedProjects[projectIndex] && (
                    <>
                      <div className="form-group">
                        <label>Project Name:</label>
                        <input
                          type="text"
                          value={project.name || ''}
                          onChange={e => {
                            updateProjectName(projectIndex, e.target.value);
                          }}
                          placeholder="Project name"
                        />
                      </div>

                      <div className="form-group">
                        <label>GitHub Link:</label>
                        <input
                          type="text"
                          value={project.github_link || ''}
                          onChange={e => {
                            const newProjects = [...fields.projects];
                            newProjects[projectIndex].github_link = e.target.value;
                            updateField('projects', newProjects);
                          }}
                          placeholder="GitHub repository URL"
                        />
                      </div>

                      <div className="form-group">
                        <label>Description:</label>
                        <textarea
                          value={project.description || ''}
                          onChange={e => {
                            const newProjects = [...fields.projects];
                            newProjects[projectIndex].description = e.target.value;
                            updateField('projects', newProjects);
                          }}
                          placeholder="Project description"
                          rows={3}
                        />
                      </div>
                    </>
                  )}
                </div>
              ))}

              <button
                type="button"
                className="add-company-button"
                onClick={() => {
                  const newProjects = [...(fields.projects || [])];
                  newProjects.push({
                    name: '',
                    description: '',
                    github_link: ''
                  });
                  updateField('projects', newProjects);

                  // Auto-expand the newly added project
                  const newProjectIndex = newProjects.length - 1;
                  setExpandedProjects(prev => ({
                    ...prev,
                    [newProjectIndex]: true
                  }));
                }}
              >
                + Add Project
              </button>
            </div>
          )}
        </div>
      )}

      {/* Skills Section */}
      {(!showOnlySections || showOnlySections.includes("skills")) && (
        <div className="company-section">
          <SectionHeader
            title="Skills"
            section="skills"
            expanded={expandedSections.skills}
          />

          {expandedSections.skills && (
            <div className="resume-form-section-content">
              {fields.is_tech_job ? (
                // Tech job: Show Programming Languages, Applications, Technical Skills
                <>
                  <div className="skills-section-form-group">
                    <label>Programming Languages:</label>
                    <input
                      type="text"
                      value={fields.programming_languages || ''}
                      onChange={e => updateField('programming_languages', e.target.value)}
                      placeholder="e.g., Python, JavaScript, Java"
                    />
                  </div>

                  <div className="skills-section-form-group">
                    <label>Applications:</label>
                    <input
                      type="text"
                      value={fields.applications || ''}
                      onChange={e => updateField('applications', e.target.value)}
                      placeholder="e.g., Docker, Git, VSCode"
                    />
                  </div>

                  <div className="skills-section-form-group">
                    <label>Technical Skills:</label>
                    <input
                      type="text"
                      value={fields.technical_skills || ''}
                      onChange={e => updateField('technical_skills', e.target.value)}
                      placeholder="e.g., Machine Learning, Cloud Computing"
                    />
                  </div>
                </>
              ) : (
                // Non-tech job: Show Technical Skills, Non-Technical Skills, Domain Experience
                <>
                  <div className="skills-section-form-group">
                    <label>Technical Skills:</label>
                    <input
                      type="text"
                      value={fields.technical_skills || ''}
                      onChange={e => updateField('technical_skills', e.target.value)}
                      placeholder="e.g., Microsoft Office, Data Analysis, Project Management Tools"
                    />
                  </div>

                  <div className="skills-section-form-group">
                    <label>Soft Skills:</label>
                    <input
                      type="text"
                      value={fields.non_technical_skills || ''}
                      onChange={e => updateField('non_technical_skills', e.target.value)}
                      placeholder="e.g., Leadership, Communication, Problem Solving"
                    />
                  </div>

                  <div className="skills-section-form-group">
                    <label>Domain Experience:</label>
                    <input
                      type="text"
                      value={fields.domain_experience || ''}
                      onChange={e => updateField('domain_experience', e.target.value)}
                      placeholder="e.g., Healthcare Regulations, Financial Compliance, Marketing Strategy"
                    />
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/* Education Section */}
      {(!showOnlySections || showOnlySections.includes("education")) && (
        <div className="company-section">
          <SectionHeader
            title="Education"
            section="education"
            expanded={expandedSections.education}
          />

          {expandedSections.education && (
            <div className="resume-form-section-content">
              {fields.education && fields.education.map((edu, eduIndex) => (
                <div key={`edu-${eduIndex}`} className="education-section" style={{ opacity: edu.omitted ? 0.5 : 1 }}>
                  <div className="education-header">
                    <h4>{edu.degree || 'Education'}</h4>
                    <div className="toggle-container">
                      <label className="toggle-switch" title={edu.omitted ? "Include in resume" : "Exclude from resume"}>
                        <input
                          type="checkbox"
                          checked={!edu.omitted}
                          onChange={() => onToggleEducation(eduIndex)}
                        />
                        <span className="slider"></span>
                      </label>
                      {edu.omitted && <span className="toggle-label">Hidden</span>}
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Degree:</label>
                    <input
                      type="text"
                      value={edu.degree || ''}
                      onChange={e => {
                        const newEducation = [...fields.education];
                        newEducation[eduIndex].degree = e.target.value;
                        updateField('education', newEducation);
                      }}
                      placeholder="e.g., Bachelor of Science in Computer Science"
                    />
                  </div>

                  <div className="form-group">
                    <label>School:</label>
                    <input
                      type="text"
                      value={edu.school || ''}
                      onChange={e => {
                        const newEducation = [...fields.education];
                        newEducation[eduIndex].school = e.target.value;
                        updateField('education', newEducation);
                      }}
                      placeholder="University name"
                    />
                  </div>

                  <div className="form-row">
                    <div className="form-group half">
                      <label>Year:</label>
                      <input
                        type="text"
                        value={edu.year || ''}
                        onChange={e => {
                          const newEducation = [...fields.education];
                          newEducation[eduIndex].year = e.target.value;
                          updateField('education', newEducation);
                        }}
                        placeholder="YYYY"
                      />
                    </div>

                    <div className="form-group half">
                      <label>GPA:</label>
                      <input
                        type="text"
                        value={edu.gpa || ''}
                        onChange={e => {
                          const newEducation = [...fields.education];
                          newEducation[eduIndex].gpa = e.target.value;
                          updateField('education', newEducation);
                        }}
                        placeholder="Optional"
                      />
                    </div>
                  </div>
                </div>
              ))}

              <button
                type="button"
                className="add-education-button"
                onClick={() => {
                  const newEducation = [...(fields.education || [])];
                  newEducation.push({
                    degree: '',
                    school: '',
                    year: '',
                    gpa: ''
                  });
                  updateField('education', newEducation);
                }}
              >
                + Add Education
              </button>
            </div>
          )}
        </div>
      )}


    </form>
  );
};

export default ResumeEditForm;
